import { useEffect, useState } from 'react';
import { useBodyClass } from '@/hooks/useBodyClass';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronDown, Sparkles, ExternalLink, Loader2 } from 'lucide-react';
import {
  getMealBreakdown, breakdownVerdict,
  type MealBreakdown, type BreakRow,
} from '@/lib/mealBreakdown';
import {
  getNoteForNutrient, evidenceLabel, RESEARCH_DISCLAIMER,
  type ResearchNote,
} from '@/lib/researchNotes';

/**
 * Разбор приёма целиком (29.09.2026, замысел Славы).
 *
 * Было: «Узнать пользу продуктов» → модалка со списком нутриентов, к каждому
 * описание от модели. Чем богаче приём, тем длиннее простыня.
 *
 * Стало — четыре уровня, и без прокрутки видно только первый:
 *   1. одна фраза-вердикт и ряд чипов с главными нутриентами;
 *   2. свёрнутые блоки: витамины, минералы, клетчатка и основа;
 *   3. «Что говорят учёные» — заметка из `research_notes` под главный нутриент;
 *   4. в каждой строке — зачем это телу.
 *
 * Числа берутся из базы (их досчитал `enrich-meal`), поэтому открывается мгновенно
 * и не стоит ничего. Модель тут больше не нужна вовсе.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  mealId: string | null;
  mealName: string;
  title: string;
  /** Пнуть досчёт, если в приёме есть позиции без микронутриентов */
  onKickEnrich?: () => void;
}

function Bar({ percent }: { percent: number }) {
  const width = Math.min(100, percent);
  return (
    <div className="h-1.5 rounded-full bg-bg-elevated overflow-hidden">
      <div
        className="h-full rounded-full bg-emerald-glow/80"
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

function Row({ row }: { row: BreakRow }) {
  return (
    <div className="py-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm">
          {row.emoji} {row.label}
        </span>
        <span className="text-xs text-ink-dim tabular-nums whitespace-nowrap">
          ~{row.amount} {row.unit} · {row.percent}%
        </span>
      </div>
      <div className="mt-1.5"><Bar percent={row.percent} /></div>
      {row.benefit && (
        <div className="mt-1 text-[11px] leading-snug text-ink-muted">{row.benefit}</div>
      )}
    </div>
  );
}

function Block({
  title, rows, defaultOpen = false,
}: { title: string; rows: BreakRow[]; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!rows.length) return null;
  const top = [...rows].sort((a, b) => b.percent - a.percent)[0];
  return (
    <div className="border-t border-line/40">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full py-3 flex items-center justify-between gap-2 text-left"
      >
        <span className="text-sm font-medium">{title}</span>
        <span className="flex items-center gap-2 text-xs text-ink-dim">
          {!open && <span>{top.emoji} {top.percent}%</span>}
          <ChevronDown
            size={15}
            className={`transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pb-2">
              {rows.map((r) => <Row key={r.key} row={r} />)}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ScienceBlock({ note }: { note: ResearchNote }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-line/40">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full py-3 flex items-center justify-between gap-2 text-left"
      >
        <span className="text-sm font-medium">Что говорят учёные</span>
        <ChevronDown
          size={15}
          className={`text-ink-dim transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pb-3 space-y-2">
              <p className="text-sm leading-snug">{note.claim}</p>
              <p className="text-xs leading-relaxed text-ink-dim">{note.detail}</p>
              {note.caveat && (
                <p className="text-[11px] leading-snug text-ink-muted">{note.caveat}</p>
              )}
              <a
                href={note.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-[11px] text-emerald-glow"
              >
                {note.authors}, {note.journal}, {note.year}
                <ExternalLink size={11} />
              </a>
              <div className="text-[11px] text-ink-muted">{evidenceLabel(note)}</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function MealBreakdownModal({
  open, onClose, mealId, mealName, title, onKickEnrich,
}: Props) {
  // Экраны лежат в слое ниже нижней панели (z-10 против z-30): без этого панель
  // закрывает низ выезжающего окна вместе с его кнопками (боевой случай 10.10.2026).
  useBodyClass('modal-open', open);
  const [data, setData] = useState<MealBreakdown | null>(null);
  const [note, setNote] = useState<ResearchNote | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !mealId) return;
    let alive = true;
    setLoading(true);
    setNote(null);
    getMealBreakdown(mealId).then(async (b) => {
      if (!alive) return;
      setData(b);
      setLoading(false);
      if (b.pending) onKickEnrich?.();
      if (b.lead) {
        const n = await getNoteForNutrient(b.lead);
        if (alive) setNote(n);
      }
    });
    return () => { alive = false; };
  }, [open, mealId]);

  const verdict = data ? breakdownVerdict(data) : null;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 z-40 backdrop-blur-sm"
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-50 max-h-[90vh] overflow-y-auto bg-bg-card rounded-t-3xl border-t border-line max-w-md mx-auto"
          >
            <div className="sticky top-0 bg-bg-card border-b border-line/40 px-5 py-3 flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <Sparkles size={18} className="text-emerald-glow" />
                <div>
                  <div className="font-display font-bold text-base leading-tight">{title}</div>
                  {mealName && <div className="text-xs text-ink-dim line-clamp-1">{mealName}</div>}
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-bg-elevated text-ink-muted flex items-center justify-center shrink-0"
              >
                <X size={16} />
              </button>
            </div>

            <div className="px-5 pb-6">
              {loading && !data && (
                <div className="py-8 flex items-center justify-center gap-2 text-sm text-ink-dim">
                  <Loader2 size={15} className="animate-spin" /> Собираю разбор…
                </div>
              )}

              {data && (
                <>
                  {verdict && (
                    <p className="pt-4 pb-3 text-sm leading-snug">{verdict}</p>
                  )}

                  {data.chips.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pb-3">
                      {data.chips.map((c) => (
                        <span
                          key={c.key}
                          className="px-2.5 py-1 rounded-full bg-emerald-glow/10 border border-emerald-glow/20 text-[11px] text-emerald-glow whitespace-nowrap"
                        >
                          {c.emoji} {c.label} ~{c.percent}%
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Честно говорим, что часть позиций ещё считается, а не рисуем
                      заниженные цифры как готовый результат. */}
                  {data.pending && (
                    <div className="mb-3 flex items-center gap-2 text-[11px] text-ink-muted">
                      <Loader2 size={12} className="animate-spin" />
                      Часть продуктов ещё досчитывается — числа могут подрасти.
                    </div>
                  )}

                  {data.empty ? (
                    <p className="py-4 text-sm text-ink-dim">
                      По этому приёму витамины и минералы пока не посчитаны.
                      Загляни через минуту.
                    </p>
                  ) : (
                    <>
                      <Block title="Клетчатка и основа" rows={data.basics} defaultOpen />
                      <Block title="Минералы" rows={data.minerals} />
                      <Block title="Витамины" rows={data.vitamins} />
                      {note && <ScienceBlock note={note} />}
                    </>
                  )}

                  <p className="mt-4 text-[11px] leading-snug text-ink-muted">
                    {RESEARCH_DISCLAIMER}
                  </p>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
